/**
 * GLYDE Voice SDK - ChatWidget Component
 *
 * Floating chat widget with voice/text mode selection.
 * This is the main component for CDN auto-inject usage.
 *
 * @example
 * // Floating widget (default)
 * <ChatWidget
 *   publishableKey="pk_..."
 *   contextId="application-uuid"
 *   position="bottom-right"
 * />
 *
 * @example
 * // Modal display (opens immediately)
 * <ChatWidget
 *   publishableKey="pk_..."
 *   contextId="application-uuid"
 *   displayMode="modal"
 *   dimensions={{ width: 500, height: 600 }}
 * />
 *
 * @example
 * // Inline display (embedded in page)
 * <ChatWidget
 *   publishableKey="pk_..."
 *   contextId="application-uuid"
 *   displayMode="inline"
 *   dimensions={{ width: '100%', height: 500 }}
 * />
 *
 * @example
 * // Mobile-optimized: full viewport, safe areas, auto-start (no FAB)
 * <ChatWidget
 *   publishableKey="pk_..."
 *   contextId="application-uuid"
 *   displayMode="mobile"
 * />
 */

import React, { useState, useEffect } from 'react';
import { VoiceChat } from './VoiceChat';
import { TextChat } from './TextChat';
import type { ChatWidgetConfig, ChatMode, VoiceContextType, DisplayMode, WidgetDimensions } from '../core/types';
import { MicIcon, MessageIcon, XIcon, MinimizeIcon } from '../ui/icons';
import { componentStyles, getPositionStyles, injectGlobalStyles } from '../ui/styles';
import { toCssValue } from '../ui/utils';

/**
 * ChatWidget component props (extends ChatWidgetConfig)
 */
export interface ChatWidgetProps extends ChatWidgetConfig {
  /** Initial expanded state (for floating mode) */
  initialExpanded?: boolean;
}

/**
 * Default dimensions for different display modes
 */
const defaultDimensions: Record<DisplayMode, WidgetDimensions> = {
  floating: { width: 380, height: 520 },
  modal: { width: 480, height: 600, maxWidth: '95vw', maxHeight: '90vh' },
  inline: { width: '100%', height: 600 }, // Increased from 500 to accommodate voice controls
  mobile: { width: '100%', height: '100dvh', maxWidth: '100vw', maxHeight: '100dvh' }, // Full viewport, safe-area handled in styles
};

/**
 * ChatWidget Component
 *
 * A versatile chat widget that provides:
 * - Voice and text chat modes
 * - Mode selection toggle
 * - Multiple display modes: floating, modal, inline
 * - Configurable position and dimensions
 * - Theme support
 */
export const ChatWidget: React.FC<ChatWidgetProps> = ({
  publishableKey,
  apiKey,
  authToken,
  contextId,
  unityBaseUrl = 'https://api.glydeunity.com',
  contextType = 'discovery',
  defaultMode = 'voice',
  position = 'bottom-right',
  theme = 'light',
  allowModeSwitch = true,
  container,
  displayMode = 'floating',
  dimensions,
  showHeader = true,
  allowClose = true,
  onReady,
  onModeChange,
  onTranscript,
  onError,
  onClose,
  initialExpanded = false,
  skipContinuityLimit,
  limitToLast,
}) => {
  // Debug: Log the Unity URL being used
  console.log('[ChatWidget] Received unityBaseUrl:', unityBaseUrl);

  // For floating mode, start collapsed unless initialExpanded is true
  // For modal/inline/mobile modes, always start "expanded" (visible). Mobile auto-starts open.
  const [isExpanded, setIsExpanded] = useState(
    displayMode === 'floating' ? initialExpanded : true
  );
  const [isVisible, setIsVisible] = useState(true);

  // Default to voice if no defaultMode is set
  const [mode, setMode] = useState<ChatMode>(defaultMode || 'voice');

  // Inject global styles on mount
  useEffect(() => {
    const themeMode = theme === 'auto'
      ? (window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light')
      : theme;
    injectGlobalStyles(themeMode);
  }, [theme]);

  // Notify when ready
  useEffect(() => {
    onReady?.();
  }, [onReady]);

  // Get position styles
  const positionStyles = getPositionStyles(position);

  /**
   * Handle mode change
   */
  const handleModeChange = (newMode: ChatMode) => {
    setMode(newMode);
    onModeChange?.(newMode);
  };

  /**
   * Handle transcript from either voice or text chat
   */
  const handleTranscript = (text: string, role: 'user' | 'agent') => {
    onTranscript?.(text, role);
  };

  /**
   * Handle error from either voice or text chat
   */
  const handleError = (error: Error) => {
    onError?.(error);
  };

  /**
   * Handle close button click
   */
  const handleClose = () => {
    if (displayMode === 'floating') {
      setIsExpanded(false);
    } else {
      setIsVisible(false);
      onClose?.();
    }
  };

  // Get merged dimensions
  const mergedDimensions: WidgetDimensions = {
    ...defaultDimensions[displayMode],
    ...dimensions,
  };

  // If not visible (closed modal/inline), render nothing
  if (!isVisible) {
    return null;
  }

  // If container is specified, render inline in that container
  if (container) {
    return (
      <InlineWidget
        publishableKey={publishableKey}
        apiKey={apiKey}
        authToken={authToken}
        contextId={contextId}
        unityBaseUrl={unityBaseUrl}
        contextType={contextType}
        mode={mode}
        allowModeSwitch={allowModeSwitch}
        showHeader={showHeader}
        allowClose={allowClose}
        dimensions={mergedDimensions}
        onModeChange={handleModeChange}
        onTranscript={handleTranscript}
        onError={handleError}
        onClose={handleClose}
        skipContinuityLimit={skipContinuityLimit}
            limitToLast={limitToLast}
      />
    );
  }

  // Modal display mode
  if (displayMode === 'modal') {
    return (
      <ModalWidget
        publishableKey={publishableKey}
        apiKey={apiKey}
        authToken={authToken}
        contextId={contextId}
        unityBaseUrl={unityBaseUrl}
        contextType={contextType}
        mode={mode}
        allowModeSwitch={allowModeSwitch}
        showHeader={showHeader}
        allowClose={allowClose}
        dimensions={mergedDimensions}
        onModeChange={handleModeChange}
        onTranscript={handleTranscript}
        onError={handleError}
        onClose={handleClose}
        skipContinuityLimit={skipContinuityLimit}
            limitToLast={limitToLast}
      />
    );
  }

  // Mobile display mode: full viewport with safe areas, no FAB, auto-start
  if (displayMode === 'mobile') {
    return (
      <MobileWidget
        publishableKey={publishableKey}
        apiKey={apiKey}
        authToken={authToken}
        contextId={contextId}
        unityBaseUrl={unityBaseUrl}
        contextType={contextType}
        mode={mode}
        allowModeSwitch={allowModeSwitch}
        showHeader={showHeader}
        allowClose={allowClose}
        dimensions={mergedDimensions}
        onModeChange={handleModeChange}
        onTranscript={handleTranscript}
        onError={handleError}
        onClose={handleClose}
        skipContinuityLimit={skipContinuityLimit}
            limitToLast={limitToLast}
      />
    );
  }

  // Inline display mode (no container specified, renders in place)
  if (displayMode === 'inline') {
    return (
      <div
        className="glyde-widget-root"
        style={{
          width: toCssValue(mergedDimensions.width),
          height: toCssValue(mergedDimensions.height),
          maxWidth: toCssValue(mergedDimensions.maxWidth),
          maxHeight: toCssValue(mergedDimensions.maxHeight),
          minHeight: '500px', // Ensure enough height for voice controls
        }}
      >
        <InlineWidget
          publishableKey={publishableKey}
          apiKey={apiKey}
          authToken={authToken}
          contextId={contextId}
          unityBaseUrl={unityBaseUrl}
          contextType={contextType}
          mode={mode}
          allowModeSwitch={allowModeSwitch}
          showHeader={showHeader}
          allowClose={allowClose}
          dimensions={mergedDimensions}
          onModeChange={handleModeChange}
          onTranscript={handleTranscript}
          onError={handleError}
          onClose={handleClose}
          skipContinuityLimit={skipContinuityLimit}
          limitToLast={limitToLast}
        />
      </div>
    );
  }

  // Default: Floating display mode
  return (
    <div className="glyde-widget-root">
      {/* Floating Button */}
      {!isExpanded && (
        <button
          onClick={() => setIsExpanded(true)}
          style={{
            ...componentStyles.floatingButton,
            ...positionStyles.button,
            background: 'linear-gradient(135deg, var(--glyde-primary, #c026d3) 0%, var(--glyde-secondary, #7c3aed) 100%)',
          }}
          aria-label="Open chat"
        >
          <MicIcon size={28} color="white" />
        </button>
      )}

      {/* Expanded Panel */}
      {isExpanded && (
        <div
          style={{
            ...componentStyles.widgetPanel,
            ...positionStyles.panel,
            background: 'var(--glyde-background, white)',
          }}
          className="glyde-slide-up"
        >
          {/* Header with close button */}
          <WidgetHeader
            contextType={contextType}
            showMinimize={true}
            showClose={true}
            onMinimize={() => setIsExpanded(false)}
            onClose={handleClose}
          />

          {/* Mode Selector */}
          {allowModeSwitch && (
            <div style={{
              padding: '12px 16px',
              borderBottom: '1px solid var(--glyde-border, #e5e7eb)',
            }}>
              <ModeSelector mode={mode} onModeChange={handleModeChange} />
            </div>
          )}

          {/* Chat Content */}
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            {mode === 'voice' ? (
              <VoiceChat
                publishableKey={publishableKey}
                apiKey={apiKey}
                authToken={authToken}
                contextId={contextId || ''}
                unityApiUrl={unityBaseUrl}
                contextType={contextType}
                height="100%"
                onTranscript={handleTranscript}
                onError={handleError}
                onSwitchToText={allowModeSwitch ? () => handleModeChange('text') : undefined}
                skipContinuityLimit={skipContinuityLimit}
                limitToLast={limitToLast}
              />
            ) : (
              <TextChat
                publishableKey={publishableKey}
                apiKey={apiKey}
                authToken={authToken}
                contextType={contextType}
                contextId={contextId || ''}
                unityApiUrl={unityBaseUrl}
                height="100%"
                onError={handleError}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * Widget header component
 */
const WidgetHeader: React.FC<{
  contextType: VoiceContextType;
  showMinimize?: boolean;
  showClose?: boolean;
  onMinimize?: () => void;
  onClose?: () => void;
}> = ({ contextType, showMinimize, showClose, onMinimize, onClose }) => (
  <div style={{
    ...componentStyles.header,
    background: 'linear-gradient(135deg, var(--glyde-primary, #c026d3) 0%, var(--glyde-secondary, #7c3aed) 100%)',
    color: 'white',
    borderTopLeftRadius: '16px',
    borderTopRightRadius: '16px',
  }}>
    <div>
      <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 600 }}>
        GLYDE Assistant
      </h3>
      <small style={{ opacity: 0.8, fontSize: '0.75rem' }}>
        {contextType === 'screening' ? 'Candidate Screening' : 'AI Assistant'}
      </small>
    </div>
    <div style={{ display: 'flex', gap: '8px' }}>
      {showMinimize && onMinimize && (
        <button
          onClick={onMinimize}
          style={{
            ...componentStyles.iconButton,
            background: 'rgba(255,255,255,0.2)',
            width: '32px',
            height: '32px',
          }}
          aria-label="Minimize"
        >
          <MinimizeIcon size={16} color="white" />
        </button>
      )}
      {showClose && onClose && (
        <button
          onClick={onClose}
          style={{
            ...componentStyles.iconButton,
            background: 'rgba(255,255,255,0.2)',
            width: '32px',
            height: '32px',
          }}
          aria-label="Close"
        >
          <XIcon size={16} color="white" />
        </button>
      )}
    </div>
  </div>
);

/**
 * Mode selector component (inline toggle after mode is selected)
 */
const ModeSelector: React.FC<{
  mode: ChatMode;
  onModeChange: (mode: ChatMode) => void;
}> = ({ mode, onModeChange }) => (
  <div style={componentStyles.modeSelector}>
    <button
      onClick={() => onModeChange('voice')}
      style={{
        ...componentStyles.modeSelectorButton,
        background: mode === 'voice' ? 'var(--glyde-primary, #c026d3)' : 'transparent',
        color: mode === 'voice' ? 'white' : 'var(--glyde-text, #1f2937)',
      }}
    >
      <MicIcon size={16} /> Voice
    </button>
    <button
      onClick={() => onModeChange('text')}
      style={{
        ...componentStyles.modeSelectorButton,
        background: mode === 'text' ? 'var(--glyde-secondary, #0284c7)' : 'transparent',
        color: mode === 'text' ? 'white' : 'var(--glyde-text, #1f2937)',
      }}
    >
      <MessageIcon size={16} /> Text
    </button>
  </div>
);

/**
 * Inline widget props
 */
interface InlineWidgetProps {
  publishableKey?: string;
  apiKey?: string;
  authToken?: string;
  contextId?: string;
  unityBaseUrl: string;
  contextType: VoiceContextType;
  mode: ChatMode;
  allowModeSwitch: boolean;
  showHeader?: boolean;
  allowClose?: boolean;
  dimensions?: WidgetDimensions;
  onModeChange: (mode: ChatMode) => void;
  onTranscript?: (text: string, role: 'user' | 'agent') => void;
  onError?: (error: Error) => void;
  onClose?: () => void;
  skipContinuityLimit?: boolean;
  /** Max last N messages when skipContinuityLimit is false (default 3; cdn-test use 5; CareerBuddy use 50). */
  limitToLast?: number;
}

/**
 * Inline widget (non-floating) for custom container usage or inline display
 */
const InlineWidget: React.FC<InlineWidgetProps> = ({
  publishableKey,
  apiKey,
  authToken,
  contextId,
  unityBaseUrl,
  contextType,
  mode,
  allowModeSwitch,
  showHeader = true,
  allowClose = true,
  dimensions,
  onModeChange,
  onTranscript,
  onError,
  onClose,
  skipContinuityLimit,
  limitToLast,
}) => {
  const [currentMode, setCurrentMode] = useState<ChatMode>(mode);

  // Update current mode when prop changes
  useEffect(() => {
    setCurrentMode(mode);
  }, [mode]);

  const handleModeChange = (newMode: ChatMode) => {
    setCurrentMode(newMode);
    onModeChange(newMode);
  };

  return (
    <div
      className="glyde-widget-root"
      style={{
        height: toCssValue(dimensions?.height) || '100%',
        width: toCssValue(dimensions?.width) || '100%',
        maxWidth: toCssValue(dimensions?.maxWidth),
        maxHeight: toCssValue(dimensions?.maxHeight),
        minHeight: '500px', // Ensure voice controls are visible
        display: 'flex',
        flexDirection: 'column',
        borderRadius: '16px',
        overflow: 'hidden',
        boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
        border: '1px solid var(--glyde-border, #e5e7eb)',
        background: 'var(--glyde-background, white)',
      }}
    >
      {/* Header */}
      {showHeader && (
        <WidgetHeader
          contextType={contextType}
          showMinimize={false}
          showClose={allowClose}
          onClose={onClose}
        />
      )}

      {/* Mode Selector */}
      {allowModeSwitch && (
        <div style={{
          padding: '12px 16px',
          borderBottom: '1px solid var(--glyde-border, #e5e7eb)',
          background: 'var(--glyde-background, white)',
        }}>
          <ModeSelector mode={currentMode} onModeChange={handleModeChange} />
        </div>
      )}

      {/* Chat Content */}
      <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        {currentMode === 'voice' ? (
          <VoiceChat
            publishableKey={publishableKey}
            apiKey={apiKey}
            authToken={authToken}
            contextId={contextId || ''}
            unityApiUrl={unityBaseUrl}
            contextType={contextType}
            height="100%"
            onTranscript={onTranscript}
            onSwitchToText={allowModeSwitch ? () => handleModeChange('text') : undefined}
            onError={onError}
            skipContinuityLimit={skipContinuityLimit}
            limitToLast={limitToLast}
          />
        ) : (
          <TextChat
            publishableKey={publishableKey}
            apiKey={apiKey}
            authToken={authToken}
            contextType={contextType}
            contextId={contextId || ''}
            unityApiUrl={unityBaseUrl}
            height="100%"
            onError={onError}
          />
        )}
      </div>
    </div>
  );
};

/**
 * Modal widget - full-screen overlay with centered chat panel
 */
const ModalWidget: React.FC<InlineWidgetProps> = (props) => {
  const { dimensions, onClose } = props;

  // Handle backdrop click
  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget && props.allowClose) {
      onClose?.();
    }
  };

  // Handle escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && props.allowClose) {
        onClose?.();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose, props.allowClose]);

  return (
    <div
      className="glyde-modal-backdrop"
      onClick={handleBackdropClick}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 10000,
        padding: '20px',
      }}
    >
      <div
        className="glyde-modal-content glyde-fade-in"
        style={{
          width: toCssValue(dimensions?.width) || '480px',
          height: toCssValue(dimensions?.height) || '600px',
          maxWidth: toCssValue(dimensions?.maxWidth) || '95vw',
          maxHeight: toCssValue(dimensions?.maxHeight) || '90vh',
        }}
      >
        <InlineWidget {...props} />
      </div>
    </div>
  );
};

/**
 * Mobile widget - full-viewport chat for mobile with safe areas and auto-start.
 * No floating button; chat opens immediately and uses 100dvh + safe-area insets.
 */
const MobileWidget: React.FC<InlineWidgetProps> = (props) => {
  const { onClose, dimensions } = props;

  // Inner widget fills the viewport container (safe-area padding is on the viewport)
  const mobileDimensions: WidgetDimensions = {
    ...dimensions,
    width: '100%',
    height: '100%',
    maxWidth: '100%',
    maxHeight: '100%',
  };

  // Handle escape key (e.g. Android back)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && props.allowClose) {
        onClose?.();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose, props.allowClose]);

  return (
    <div className="glyde-mobile-viewport">
      <InlineWidget {...props} dimensions={mobileDimensions} />
    </div>
  );
};

export default ChatWidget;
